import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useTeamRoles } from "@/pages/inventory/Requisitions";
import {
  ClipboardText,
  Warning,
  Clock,
  ArrowRight,
  CheckCircle,
  X,
  ArrowClockwise,
} from "@phosphor-icons/react";

/**
 * Store-team Queue widget for the Inventory Dashboard.
 *
 * Renders ONLY for users who can approve (store / admin / inventory). For
 * production-only or read-only users the component returns null so we don't
 * clutter their dashboard with actions they can't take.
 */

function timeAgo(hours) {
  if (hours < 1) return "just now";
  if (hours < 24) return `${Math.round(hours)}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "1d ago" : `${days}d ago`;
}

export default function StoreQueueWidget() {
  const { canApprove } = useTeamRoles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/requisitions/queue-summary?limit=6");
      setData(data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canApprove) load();
  }, [canApprove, load]);

  if (!canApprove) return null;

  if (loading && !data) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="store-queue-loading">
        <div className="text-sm text-slate-500">Loading Store approval queue…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white border border-rose-200 rounded-lg p-5" data-testid="store-queue-error">
        <div className="text-sm text-rose-600">{error}</div>
      </div>
    );
  }
  if (!data) return null;

  const { counts, queue, queue_truncated } = data;
  const empty = counts.awaiting === 0;

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg overflow-hidden"
      data-testid="store-queue-widget"
    >
      <div className={`px-5 py-4 flex items-center justify-between ${
        empty
          ? "bg-gradient-to-br from-slate-700 to-slate-800 text-white"
          : "bg-gradient-to-br from-amber-500 to-amber-600 text-white"
      }`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-white/15 flex items-center justify-center backdrop-blur-sm">
            <ClipboardText size={20} weight="duotone" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-90">
              Store Approval Queue
            </div>
            <div className="font-heading text-base font-semibold">
              {empty ? "You're all caught up" : `${counts.awaiting} requisition${counts.awaiting === 1 ? "" : "s"} awaiting review`}
            </div>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white/15 hover:bg-white/25 text-xs font-medium transition disabled:opacity-50"
          data-testid="store-queue-refresh"
          title="Refresh queue"
        >
          <ArrowClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 border-b border-slate-100">
        <Stat
          label="Awaiting"
          value={counts.awaiting}
          tone={counts.awaiting > 0 ? "amber" : "slate"}
          icon={Clock}
          testId="store-queue-stat-awaiting"
        />
        <Stat
          label="With Shortage"
          value={counts.with_shortage}
          tone={counts.with_shortage > 0 ? "rose" : "slate"}
          icon={Warning}
          testId="store-queue-stat-shortage"
        />
        <Stat
          label="Approved · 24h"
          value={counts.approved_24h}
          tone="emerald"
          icon={CheckCircle}
          testId="store-queue-stat-approved"
        />
        <Stat
          label="Rejected · 24h"
          value={counts.rejected_24h}
          tone="slate"
          icon={X}
          testId="store-queue-stat-rejected"
          hideRightBorder
        />
      </div>

      {/* Queue list */}
      {empty ? (
        <div className="px-5 py-10 text-center" data-testid="store-queue-empty">
          <CheckCircle size={36} weight="duotone" className="mx-auto text-emerald-500" />
          <div className="mt-2 text-sm font-semibold text-slate-700">Queue is clear</div>
          <div className="text-xs text-slate-500 mt-1">
            New submissions from the Production team will appear here.
          </div>
          <Link
            to="/inventory/requisitions"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 hover:text-emerald-900"
            data-testid="store-queue-view-all-empty"
          >
            View all requisitions <ArrowRight size={11} weight="bold" />
          </Link>
        </div>
      ) : (
        <>
          <div className="divide-y divide-slate-100">
            {queue.map((r) => (
              <QueueRow key={r.id} item={r} />
            ))}
          </div>
          <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs">
            <span className="text-slate-500">
              {queue_truncated
                ? `Showing 6 of ${counts.awaiting} awaiting review`
                : `Showing all ${counts.awaiting}`}
            </span>
            <Link
              to="/inventory/requisitions?status=submitted"
              className="inline-flex items-center gap-1 font-semibold text-amber-700 hover:text-amber-900"
              data-testid="store-queue-view-all"
            >
              Open full queue <ArrowRight size={11} weight="bold" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone, icon: Icon, hideRightBorder, testId }) {
  const toneMap = {
    slate: "text-slate-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
    emerald: "text-emerald-700",
  };
  return (
    <div
      className={`px-4 py-3 ${hideRightBorder ? "" : "border-r border-slate-100"}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5">
        <Icon size={11} weight="fill" className={toneMap[tone] || "text-slate-500"} />
        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">
          {label}
        </div>
      </div>
      <div className={`mt-1 text-xl font-bold tabular-nums ${toneMap[tone] || "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}

function QueueRow({ item }) {
  const stale = item.age_hours >= 24;
  return (
    <Link
      to={`/inventory/requisitions/${item.id}`}
      className="flex items-center gap-3 px-5 py-3 hover:bg-amber-50/40 transition group"
      data-testid={`store-queue-row-${item.id}`}
    >
      <div className="w-14 shrink-0">
        <div className="font-mono text-xs font-bold text-slate-900">{item.code}</div>
        <div className={`text-[10px] mt-0.5 ${stale ? "text-rose-600 font-semibold" : "text-slate-400"}`}>
          {timeAgo(item.age_hours)}
          {stale && " · stale"}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-semibold text-sm text-slate-900 truncate">{item.recipe_name}</div>
          <span className="text-[10px] text-slate-400 shrink-0">v{item.recipe_version}</span>
          {item.has_shortage && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5 shrink-0">
              <Warning size={9} weight="fill" /> Shortage
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 truncate">
          {item.batch_size} kg · Requested by {item.requested_by}
          {item.target_production_date && ` · Target ${item.target_production_date}`}
        </div>
      </div>
      <ArrowRight
        size={14}
        weight="bold"
        className="text-slate-300 group-hover:text-amber-700 group-hover:translate-x-0.5 transition-transform shrink-0"
      />
    </Link>
  );
}
